import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { StrictSchema } from "modules/bridge/utils/type-safety";
import { z } from "zod";

export interface GetMerkleProofParams {
  depositCount: number;
  networkId: number;
}

export interface MerkleProofData {
  main_exit_root: string;
  merkle_proof: string[];
  rollup_exit_root: string;
  rollup_merkle_proof: string[];
}

export interface MerkleProof {
  mainExitRoot: string;
  merkleProof: string[];
  rollupExitRoot: string;
  rollupMerkleProof: string[];
}

const apiMerkleProofToDomain = ({
  main_exit_root,
  merkle_proof,
  rollup_exit_root,
  rollup_merkle_proof,
}: MerkleProofData): MerkleProof => ({
  mainExitRoot: main_exit_root,
  merkleProof: merkle_proof,
  rollupExitRoot: rollup_exit_root,
  rollupMerkleProof: rollup_merkle_proof,
});

const merkleProofParser = StrictSchema<MerkleProofData, MerkleProof>()(
  z
    .object({
      main_exit_root: z.string().length(66),
      merkle_proof: z.array(z.string().length(66)),
      rollup_exit_root: z.string().length(66),
      rollup_merkle_proof: z.array(z.string().length(66)),
    })
    .transform(apiMerkleProofToDomain)
);

const getMerkleProofResponseParser = StrictSchema<
  {
    proof: {
      main_exit_root: string;
      merkle_proof: string[];
      rollup_exit_root: string;
      rollup_merkle_proof: string[];
    };
  },
  {
    proof: MerkleProof;
  }
>()(
  z.object({
    proof: merkleProofParser,
  })
);

const fetchMerkleProof = async ({
  depositCount,
  networkId,
}: GetMerkleProofParams): Promise<MerkleProof> => {
  const response = await axios.get(`/api/merkle-proof`, {
    params: {
      deposit_cnt: depositCount,
      net_id: networkId,
    },
  });

  const parsedData = getMerkleProofResponseParser.safeParse(response.data);

  if (parsedData.success) {
    return parsedData.data.proof;
  } else {
    throw parsedData.error;
  }
};

export const useMerkleProof = (params: GetMerkleProofParams) => {
  const { depositCount, networkId } = params;

  return useQuery({
    queryKey: ["merkleProof", depositCount, networkId],
    queryFn: () => fetchMerkleProof(params),
    enabled: !!depositCount && !!networkId,
    retry: 3,
    refetchOnWindowFocus: false,
    staleTime: 60 * 13,
  });
};
